import React from 'react';
import { cn } from '@/lib/utils';
import { ActivityItemProps } from '../utils/dashboardTypes';
import { Briefcase, Users, UserCheck, MapPin, Clock, ArrowUpRight } from 'lucide-react';

export function ActivityItem({ type, title, subtitle, timestamp, onClick }: ActivityItemProps) {
  // Type-specific styling
  const typeConfig = {
    role: {
      icon: Briefcase,
      label: 'Role',
      accentColor: 'bg-amber-500',
      iconBg: 'bg-amber-50 dark:bg-amber-950/40',
      iconColor: 'text-amber-600 dark:text-amber-400',
      hoverBorder: 'hover:border-amber-200 dark:hover:border-amber-800',
    },
    candidate: {
      icon: Users,
      label: 'Candidate',
      accentColor: 'bg-violet-500',
      iconBg: 'bg-violet-50 dark:bg-violet-950/40',
      iconColor: 'text-violet-600 dark:text-violet-400',
      hoverBorder: 'hover:border-violet-200 dark:hover:border-violet-800',
    },
    match: {
      icon: UserCheck,
      label: 'Match',
      accentColor: 'bg-teal-500',
      iconBg: 'bg-teal-50 dark:bg-teal-950/40',
      iconColor: 'text-teal-600 dark:text-teal-400',
      hoverBorder: 'hover:border-teal-200 dark:hover:border-teal-700',
    },
  };

  const config = typeConfig[type];
  const IconComponent = config.icon;

  // Format relative timestamp
  const getRelativeTime = (isoString: string) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div
      onClick={onClick}
      className={cn(
        "group relative cursor-pointer",
        "flex items-start gap-4",
        "p-4 rounded-xl",
        "bg-white dark:bg-slate-800/60",
        "border border-slate-200/80 dark:border-slate-700/60",
        "transition-all duration-200",
        config.hoverBorder,
        "hover:shadow-lg hover:-translate-y-0.5",
        "active:translate-y-0 active:shadow-md"
      )}
    >
      {/* Accent line on left */}
      <div className={cn(
        "absolute left-0 top-3 bottom-3 w-1 rounded-full",
        config.accentColor,
        "opacity-0 group-hover:opacity-100",
        "transition-opacity duration-200"
      )} />

      {/* Icon container */}
      <div className={cn(
        "relative flex-shrink-0",
        "w-12 h-12 rounded-xl",
        "flex items-center justify-center",
        config.iconBg,
        "transition-transform duration-200",
        "group-hover:scale-105"
      )}>
        <IconComponent className={cn("w-5 h-5", config.iconColor)} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 py-0.5">
        {/* Title row */}
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className={cn(
            "font-semibold text-[15px] leading-tight",
            "text-slate-800 dark:text-slate-100",
            "truncate",
            "group-hover:text-slate-900 dark:group-hover:text-white",
            "transition-colors duration-200"
          )}>
            {title}
          </h4>

          {/* Arrow indicator */}
          <ArrowUpRight className={cn(
            "w-4 h-4 flex-shrink-0 mt-0.5",
            "text-slate-300 dark:text-slate-600",
            "group-hover:text-slate-500 dark:group-hover:text-slate-400",
            "group-hover:translate-x-0.5 group-hover:-translate-y-0.5",
            "transition-all duration-200"
          )} />
        </div>

        {/* Metadata row */}
        <div className="flex items-center gap-3 text-sm">
          {/* Subtitle with location icon for roles */}
          <span className={cn(
            "flex items-center gap-1.5",
            "text-slate-500 dark:text-slate-400",
            "truncate"
          )}>
            {type === 'role' && <MapPin className="w-3.5 h-3.5 flex-shrink-0" />}
            <span className="truncate">{subtitle}</span>
          </span>

          {/* Separator dot */}
          <span className="w-1 h-1 rounded-full bg-slate-300 dark:bg-slate-600 flex-shrink-0" />

          {/* Timestamp */}
          <span className={cn(
            "flex items-center gap-1.5 flex-shrink-0",
            "text-slate-400 dark:text-slate-500",
            "text-xs"
          )}>
            <Clock className="w-3 h-3" />
            {getRelativeTime(timestamp)}
          </span>
        </div>
      </div>
    </div>
  );
}
